import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { Button, Divider, Group, Stack, Text, TextInput, PasswordInput, Alert } from '@mantine/core'
import { emailIsValid } from '../util/validation'
import CurrentUser from '../graphql/queries/CurrentUser'
import LoginWithEmail from '../graphql/queries/LoginWithEmail'
import { useModalStack } from '../ui/ModalStack'
import { Actions } from '../AppReducer'
import { useAppDispatch } from '../AppContext'
import GoogleLoginButton from './GoogleLoginButton'
import AppleLoginButton from './AppleLoginButton'

export default function EmailLogin(props) {
  const { onClose, roomId } = props
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { pushModal } = useModalStack()
  const dispatch = useAppDispatch()

  const [loginMutation, { loading, data, error, reset }] = useMutation(LoginWithEmail, {
    fetchPolicy: 'no-cache',
    update: addUserToCache
  })

  useEffect(() => {
    if (data?.loginWithEmail?.accessToken) {
      const { loginWithEmail: { accessToken, refreshToken, user } } = data
      localStorage.setItem('at', accessToken)
      if (refreshToken) localStorage.setItem('rt', refreshToken)
      if (user?.email) localStorage.setItem('email', user.email)
      reset()
      dispatch(Actions.logIn())
      // Navigate to room if we have a roomId (from invite acceptance)
      if (roomId) {
        dispatch(Actions.setCurrentRoom(roomId))
      }
      onClose?.()
      // Alert unverified, non-guest users to check their email
      const roles = user?.roles ?? []
      if (!roles.includes('verified') && !roles.includes('guest')) {
        pushModal('unverifiedAccount', { email: user?.email })
      }
    }
  }, [data, dispatch, onClose, pushModal, reset, roomId])

  useEffect(() => {
    if (error) {
      console.error('Login error:', JSON.stringify(error, null, 2))
      if (error.networkError?.statusCode === 401) {
        setErrorMessage('Invalid email address or password. Please try again.')
      } else {
        setErrorMessage(error.message)
      }
      reset()
    }
  }, [error, reset])

  function addUserToCache(cache, { data }) {
    cache.writeQuery({
      query: CurrentUser,
      data: data.loginWithEmail
    })
  }

  function emailUpdated(e) {
    setErrorMessage('')
    setEmail(e.target.value)
  }

  function passwordUpdated(e) {
    setErrorMessage('')
    setPassword(e.target.value)
  }

  function validateEmail() {
    if (!emailIsValid(email)) return 'Invalid email address.'
    return null
  }

  function validatePassword() {
    if (password.length < 1) return 'You must supply a password to log in.'
    return null
  }

  async function logIn() {
    const emailProblem = validateEmail()
    if (emailProblem) { setErrorMessage(emailProblem); return }
    const passwordProblem = validatePassword()
    if (passwordProblem) { setErrorMessage(passwordProblem); return }
    try {
      await loginMutation({ variables: { email, password } })
    } catch (err) {
      setErrorMessage(err.message)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !loading) {
      logIn()
    }
  }

  return (
    <Stack gap="sm" style={{ marginTop: '1em' }}>
      <GoogleLoginButton onClose={onClose} roomId={roomId} />
      <AppleLoginButton onClose={onClose} roomId={roomId} />
      <Divider label="or continue with email" labelPosition="center" my="xs" />
      <TextInput
        label="Email"
        placeholder="you@example.com"
        value={email}
        onChange={emailUpdated}
        type="email"
        onKeyDown={handleKeyDown}
      />
      <PasswordInput
        label="Password"
        placeholder="Your password"
        value={password}
        onChange={passwordUpdated}
        onKeyDown={handleKeyDown}
      />
      { errorMessage ? (
        <Alert color="red" variant="light">
          <Text size="sm">{errorMessage}</Text>
        </Alert>
      ) : null }
      <Group justify="space-between" mt="xs">
        <Button variant="subtle" onClick={() => {
          // Save email to localStorage before opening forgot password modal
          if (email) localStorage.setItem('email', email)
          pushModal('forgotPassword')
        }}>
          Forgot Password
        </Button>
        <Group>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={loading} onClick={logIn}>
            Log In
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}

